import { GridApi } from '@ag-grid-community/core';
import { Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { UserRecord } from '@tassat/fred-definitions';
import {
    ClientUser as ClientUserIcon, Lock as LockIcon, SsoUser as SsoUserIcon, Wallet as WalletIcon
} from '@tassat/icon';
import { AttributeTag } from '@tassat/symbols';
import React from 'react';

import { GridUserData } from 'Containers/admin/pages/firms/firmView/firmUsers';
import history from 'Lib/browserHistory';
import { LOCK_ICON_COLOR } from 'Lib/constant';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { GridModelContext } from 'SharedComponents/sideList';

interface ClientUserCellRendererProps {
    data: UserRecord | GridUserData;
    api: GridApi;
    cellHeight: number;
    isSSOEnabled: boolean;
}

const muiStyles = makeStyles((theme: ApplicationMuiTheme) => ({
    root: {
        minHeight: '60px',
        maxHeight: '122px',
        height: (props: ClientUserCellRendererProps) => props.cellHeight,
        color: 'inherit',
        textDecoration: 'inherit',
        padding: ' 0 2.5rem',
        backgroundColor: (props: ClientUserCellRendererProps) => (props.data.IsLocked ?
            theme?.app?.applicationColors.tableRowWarningColor || 'rgba(245,130,74,0.16)'
            : theme?.app?.applicationColors.enumColors.white || '#fff'),
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme?.app?.applicationColors.enumColors.offWhite ||
                '#F3F3F3'
        }
    },
    name: {
        fontSize: '1.3rem',
        lineHeight: '1.5rem',
        color: theme?.app?.clientTheme.colors.primaryColor ||
            '#375073',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
    },
    emailContainer: { lineHeight: 0 },
    email: {
        fontSize: '.9rem',
        lineHeight: '1.2rem',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        paddingTop: '5px'
    },
    statusIconContainer: {
        width: 'auto',
        alignItems: 'center'
    },
    statusIcon: {
        width: 'auto',
        height: '22px',
        minWidth: '22px',
        paddingLeft: '10px',
        boxSizing: 'content-box'
    }
}));

export function ClientUserCellRenderer(props: ClientUserCellRendererProps) {
    const gridModel = React.useContext(GridModelContext);

    const {
        data,
        api,
        isSSOEnabled
    } = props;

    const classes = muiStyles(props);
    const fullName = `${data.FirstName} ${data.LastName}`;

    const handleUserClick = (): void => {
        history.push(`/admin/firms/${data.FirmId}/users/${data.UserId}`,
            {
                sortModel: gridModel.sortModel ?? api?.getColumnState()
                    .find(({ colId }) => colId === gridModel?.sortModel?.colId)
            });
    };

    return (
        <Grid
            alignItems='center'
            className={`${classes.root} firm-info-cell`}
            container
            direction='row'
            justifyContent='space-around'
            onClick={handleUserClick}
            title={fullName.length > 36 ? fullName : null}
            wrap='nowrap'
        >
            <Grid
                container
                direction='column'
                item
                justifyContent='flex-start'
                spacing={0}
                wrap='nowrap'
                zeroMinWidth
            >
                <Typography
                    className={classes.name}
                    variant='h3'
                ><strong>{fullName}</strong>
                </Typography>
                {
                    data.Username &&
                    <Grid
                        className={classes.emailContainer}
                        container
                        direction='row'
                        item
                        spacing={1}
                        wrap='nowrap'
                    >
                        <Grid item>
                            <ClientUserIcon id='userDetailsUsername' />
                        </Grid>
                        <Grid
                            item
                            xs={10}
                        >
                            <Typography
                                className={classes.email}
                                variant='body2'
                            >
                                {data.Username}
                            </Typography>
                        </Grid>
                    </Grid>
                }
                {
                    isSSOEnabled &&
                    data.Username &&
                    <Grid
                        className={classes.emailContainer}
                        container
                        direction='row'
                        item
                        spacing={1}
                        wrap='nowrap'
                    >
                        <Grid item>
                            <SsoUserIcon id='userDetailsSsoUserId' />
                        </Grid>
                        <Grid
                            item
                            xs={10}
                        >
                            <Typography
                                className={classes.email}
                                variant='body2'
                            >
                                {data.Username}
                            </Typography>
                        </Grid>
                    </Grid>
                }
            </Grid>
            <Grid
                container
                direction='row'
                item
                justifyContent='flex-end'
                spacing={0}
                wrap='nowrap'
                xs
            >
                {data.IsLocked && <LockIcon
                    className={classes.statusIcon}
                    iconColor={LOCK_ICON_COLOR}
                    id='userListLockedIcon'
                />}
                {data.IsFrozen && <WalletIcon
                    className={classes.statusIcon}
                    iconColor={LOCK_ICON_COLOR}
                    id='userListFrozenIcon'
                />}
                {
                    ('HasApiConnections' in data && data.HasApiConnections) ||
                    data.IsTokenAPIUser ?
                        <AttributeTag
                            attribute='API'
                            className={classes.statusIcon}
                            id='userListApiIcon'
                        />
                        : null
                }
            </Grid>
        </Grid>
    );
}

export default (ClientUserCellRenderer);